<script>
export default {
  title: 'Project 4: Working with the Symbolic Math Toolbox',
  starterId: null
}
</script>

<template>
  <section>

    <p>This programming assignment introduces you to MATLAB's Symbolic Math
      Toolbox.</p>

    <article>
      <h2>Readings</h2>
      <ul>
        <li><a href="https://www.mathworks.com/help/symbolic/create-symbolic-numbers-variables-and-expressions.html"><cite>MATLAB Documentation</cite> Symbolic Numbers, Variables, and Expressions</a></li>
        <li><a href="https://www.mathworks.com/help/symbolic/create-symbolic-functions.html"><cite>MATLAB Documentation</cite> Create Symbolic Functions</a></li>
        <li><a href="https://www.mathworks.com/help/symbolic/create-symbolic-matrices.html"><cite>MATLAB Documentation</cite> Create Symbolic Matrices</a></li>
        <li><a href="https://www.mathworks.com/help/symbolic/performing-symbolic-computations.html"><cite>MATLAB Documentation</cite> Perform Symbolic Computations</a></li>
        <li><a href="https://www.mathworks.com/help/symbolic/assumptions-for-symbolic-objects.html"><cite>MATLAB Documentation</cite> Use Assumptions on Symbolic Variables</a></li>
        <li>Reference: <a href="https://www.mathworks.com/help/symbolic/index.html"><cite>MATLAB Documentation</cite> Symbolic Math Toolbox</a></li>
      </ul>
    </article>

    <article>
      <h2>Background</h2>

      <p>This assignment has been partially adapted from lab exercises designed
        by Professor Eric Woolgar at the University of Alberta.</p>

      <p>Here are some examples of relevant MATLAB commands:</p>

      <table style="width: 100%">
        <tbody><tr style="vertical-align: top">
          <td style="min-width: 25%; padding: 8px 0"><code>x = sym('x')</code></td>
          <td style="padding: 8px 0">defines <code>x</code> to be a symbolic object that can then be treated as a mathematical variable.</td>
        </tr>
        <tr style="vertical-align: top">
          <td style="padding: 8px 0"><code>syms('a', 'b', 'c', 'd)</code></td>
          <td style="padding: 8px 0">creates several symbolic objects at once; <code>syms('a')</code> is the same as <code>a = sym('a')</code>.</td>
        </tr>
        <tr style="vertical-align: top">
          <td style="padding: 8px 0"><code>syms a b c d</code></td>
          <td style="padding: 8px 0">same as <code>syms('a', 'b', 'c', 'd')</code>.</td>
        </tr>
        <tr style="vertical-align: top">
          <td style="padding: 8px 0"><code>y = sym('1/2')</code></td>
          <td style="padding: 8px 0">creates the symbolic object <code>y</code> and then gives it the definite value <sup>1</sup>⁄<sub>2</sub>, so <code>y</code> is treated as a mathematical constant.</td>
        </tr>
        <tr style="vertical-align: top">
          <td style="padding: 8px 0"><code>M = [a b; c d]</code></td>
          <td style="padding: 8px 0">creates a symbolic matrix, if <code>a</code>, <code>b</code>, <code>c</code>, and <code>d</code> are symbolic objects.</td>
        </tr>
        <tr style="vertical-align: top">
          <td style="padding: 8px 0"><code>ezplot(y, [a b])</code></td>
          <td style="padding: 8px 0">plots the symbolic expression <code>y</code> (given as a function of the symbolic object <code>x</code>) on the interval from <code>a</code> to <code>b</code>.</td>
        </tr>
        <tr style="vertical-align: top">
          <td style="padding: 8px 0"><code>factor(y)</code></td>
          <td style="padding: 8px 0">factors the symbolic expression <code>y</code> (given as a function of the symbolic object <code>x</code>).</td>
        </tr>
        <tr style="vertical-align: top">
          <td style="padding: 8px 0"><code>S = solve(eqn1, eqn2, ...)</code></td>
          <td style="padding: 8px 0">solves a system of equations in symbolic variables <code>x1</code>, <code>x2</code>, ... and stores the solution in <code>S</code>.</td>
        </tr>
        <tr style="vertical-align: top">
          <td style="padding: 8px 0"><code>S.x1</code></td>
          <td style="padding: 8px 0">returns the value that <code>x1</code> takes in the solution <code>S</code>.</td>
        </tr>
        <tr style="vertical-align: top">
          <td style="padding: 8px 0"><code>simplify(S.x1)</code></td>
          <td style="padding: 8px 0">simplifies the expression for <code>x1</code>.</td>
        </tr>
        </tbody></table>
    </article>

    <article>
      <h2>Assignment</h2>

      <article>
        <h3>Exercise 1: Symbolic Root Finding</h3>
        <p>Plot the polynomial <var>y</var> = <var>x</var><sup>3</sup> –
          3<var>x</var> + 2. Factor <var>y</var> and find all values of
          <var>x</var> such that <var>y</var> = 0. Remember when defining
          <var>y</var> to first define <var>x</var> as symbolic. Also remember that
          every multiplication must be represented by an asterisk (<code>*</code>)
          and that exponents are preceded by the hat (<code>^</code>) symbol.</p>
      </article>

      <article>
        <h3>Exercise 2: Solving Systems of Equations</h3>

        <p>To solve an equation or a system of equations in the Symbolic Toolbox,
          rewrite each equation if necessary so that the right-hand side is 0. Give
          the left-hand side of each equation a name. For single equations, apply
          the <code>solve</code> command directly to this name. For systems of
          equations, first apply the <code>solve</code> command and then ask for
          the value of each unknown variable in turn.</p>

        <p>For example, to solve <var>x</var><sup>2</sup> – 3<var>x</var> + 1 =
          5, we can rewrite it as <var>x</var><sup>2</sup> – 3<var>x</var> – 4 = 0.
          Then:</p>

        <pre v-highlightjs><code class="plain">syms x
eqn = x^2 - 3*x - 4
solve(eqn)</code></pre>

        <p>Try it yourself. Notice you get two roots, of course.</p>

        <p>Next, let’s try the inhomogeneous system</p>

        <ul style="list-style-type: none">
          <li> <var>x</var><sub>1</sub> + 2<var>x</var><sub>2</sub> = 3</li>
          <li>4<var>x</var><sub>1</sub> +  <var>x</var><sub>2</sub> = –1</li>
        </ul>

        <p>Now the syntax is more complicated:</p>

        <pre v-highlightjs><code class="plain">syms x1 x2
eqn1 = x1 + 2*x2 - 3
eqn2 = 4*x1 + x2 + 2
S = solve(eqn1, eqn2) % You may use any symbol you prefer instead of S.
S.x1 This returns the value of x1 in the solution.
S.x2 This returns the value of x2 in the solution.</code></pre>

        <p>Use the Symbolic Toolbox to solve the following system (this is what
          you turn in):</p>

        <ul style="list-style-type: none">
          <li> <var>x</var><sub>1</sub> + 2<var>x</var><sub>2</sub> + 2<var>x</var><sub>3</sub> = 0</li>
          <li>2<var>x</var><sub>1</sub> + 3<var>x</var><sub>2</sub> + 4<var>x</var><sub>3</sub> = 1</li>
          <li>–<var>x</var><sub>1</sub> +  <var>x</var><sub>2</sub> +  <var>x</var><sub>3</sub> = –6</li>
        </ul>
      </article>

      <article>
        <h3>Exercise 3: Working with Symbolic Matrices</h3>

        <p>This exercise involves what Donald Knuth (<cite>The Art of Computer
          Programming, Volume 1: Fundamental Algorithms</cite>) refers to as the
          <var>n</var>-by-<var>n</var> <dfn>combinatorial matrix</dfn>:</p>

        <table style="border-color: #000; border-spacing: 5px; border-style: solid; border-width: 0 1px; margin: 0 auto; text-align: center">
          <tbody><tr><td><var>x</var> + <var>y</var></td><td><var>y</var></td><td>⋯</td><td><var>y</var></td></tr>
          <tr><td><var>y</var></td><td><var>x</var> + <var>y</var></td><td>⋯</td><td><var>y</var></td></tr>
          <tr><td>⋯</td><td>⋯</td><td>⋯</td><td>⋯</td></tr>
          <tr><td><var>y</var></td><td><var>y</var></td><td>⋯</td><td><var>x</var> + <var>y</var></td></tr>
          </tbody></table>

        <p>All of its elements are <var>y</var> except those on the main
          diagonal, which are <var>x</var>+<var>y</var>.</p>

        <p>Find an expression for the sum of all <var>n</var><sup>2</sup>
          elements of the inverse of the combinatorial matrix of size <var>n</var>.
          (Hint: it’s a simple expression in <var>n</var>, <var>x</var>, and
          <var>y</var>.) To do this, use functions in the Symbolic Toolbox and
          regular matrix functions to construct matrices of sizes 3, 4, 5, ... and
          infer a pattern.</p>
      </article>
    </article>

    <article>
      <h2>Checklist</h2>
      <p>A printed sequence of Matlab commands for each of the three exercises
        that produces a solution to the exercise.</p>
    </article>
  </section>
</template>



// WEBPACK FOOTER //
// src/components/assignments/matlab/WorkingWithTheSymbolicMathToolbox.vue